import updateBookingMeetingType from '../services/updateBookingMeetingType';
import setIsPerformingServiceUpdate from '../actions/setIsPerformingServiceUpdate';
import updateMeetingTypePrivacyAction from '../actions/updateMeetingTypePrivacyAction';
import updateMeetingTypeExtension from '../services/updateMeetingTypeExtension';
import createMeetingTypeExtension from '../services/createMeetingTypeExtension';
import setMeetingTypeExtensionDetails from '../orchestrators/setMeetingTypeExtensionDetails';
import getMeetingTypeExtensionSchedulingPolicy from './getMeetingTypeExtensionSchedulingPolicy';
import type MeetingTypeExtension from '../services/schema/MeetingTypeExtension';
import store from '../store/Store';
import { trace } from 'owa-trace';
import { isFeatureEnabled } from 'owa-feature-flags';
import updateBookingService from 'bookings-shared/lib/c1/services/updateBookingService';
import mapModelABookingService from 'bookings-shared/lib/c1/mappers/modelA/mapModelABookingService';
import { getUserConfiguration } from 'owa-session-store';
import type FeatureSource from 'bookings-shared/lib/c1/constants/source';
import type BookingService from 'bookings-shared/lib/c1/schema/BookingService';
/**
 * Triggers a service change meeting type privacy
 */
export default async function updateMeetingTypePrivacy(
    meetingTypeId: string | undefined,
    makePublic: boolean,
    excludeFromSelfService: boolean
): Promise<boolean> {
    if (meetingTypeId) {
        try {
            setIsPerformingServiceUpdate(true);
            if (store.meetingTypes) {
                updateMeetingTypePrivacyAction(meetingTypeId, makePublic, excludeFromSelfService);
                const meetingType = store.meetingTypes.get(meetingTypeId);
                if (meetingType) {
                    // Only update meetingType only if the exclude value does not match
                    if (isFeatureEnabled('bookwithme-c1B2Migration')) {
                        const bookingsSMTPAddress =
                            getUserConfiguration().SessionSettings?.UserPrincipalName || '';
                        const meetingTypeB2 = meetingType as BookingService;
                        meetingTypeB2.isPrivate = !makePublic;
                        const mappedB2Service = mapModelABookingService(meetingTypeB2);
                        await updateBookingService(
                            bookingsSMTPAddress,
                            mappedB2Service,
                            'BookWithMe'
                        );
                    } else {
                        await updateBookingMeetingType(meetingType);
                    }
                    // TODO: This update included schedulingpolicy update
                    // Could be refactored to only update if MTE.excludedFromSelfService = true
                    setMeetingTypeExtensionDetails(meetingTypeId).then(() => {
                        let requestBody: MeetingTypeExtension;
                        const meetingTypeExtension = store.meetingTypeExtension;
                        if (meetingTypeExtension) {
                            if (meetingTypeExtension.schedulingPolicy) {
                                requestBody = {
                                    itemId: meetingTypeExtension.itemId,
                                    displayName: meetingTypeExtension.displayName,
                                    isPrivate: !makePublic,
                                    recipients: [],
                                    description: meetingTypeExtension.description,
                                    additionalInformation:
                                        meetingTypeExtension.additionalInformation,
                                    schedulingPolicy: meetingTypeExtension.schedulingPolicy,
                                    durationMinutes: meetingTypeExtension.durationMinutes,
                                    isDefaultLocationOnline:
                                        meetingTypeExtension.isDefaultLocationOnline,
                                    preBuffer: meetingTypeExtension.preBuffer,
                                    postBuffer: meetingTypeExtension.postBuffer,
                                    excludeFromSelfService,
                                };
                            } else {
                                const schedulingPolicyMeetingTypeExtension =
                                    getMeetingTypeExtensionSchedulingPolicy(meetingType);
                                requestBody = {
                                    itemId: meetingTypeExtension.itemId,
                                    displayName: meetingTypeExtension.displayName,
                                    isPrivate: !makePublic,
                                    recipients: [],
                                    description: meetingType.Description,
                                    additionalInformation: meetingType.AdditionalInformation,
                                    schedulingPolicy: schedulingPolicyMeetingTypeExtension,
                                    durationMinutes: meetingType.DefaultDurationMinutes,
                                    isDefaultLocationOnline: meetingType.IsDefaultLocationOnline,
                                    preBuffer: meetingType.PreBuffer,
                                    postBuffer: meetingType.PostBuffer,
                                    excludeFromSelfService,
                                };
                            }
                            // Update meeting type extension
                            updateMeetingTypeExtension(requestBody).then(() => {
                                setIsPerformingServiceUpdate(false);
                                return true;
                            });
                        } else if (!isFeatureEnabled('bookwithme-c1B2Migration')) {
                            //Not creating meetingTypeExtensions in B2
                            const schedulingPolicyMeetingTypeExtension =
                                getMeetingTypeExtensionSchedulingPolicy(meetingType);
                            requestBody = {
                                itemId: meetingTypeId,
                                displayName: meetingType.DisplayName ?? '',
                                isPrivate: !makePublic,
                                recipients: [],
                                additionalInformation: meetingType.AdditionalInformation,
                                schedulingPolicy: schedulingPolicyMeetingTypeExtension,
                                durationMinutes: meetingType.DefaultDurationMinutes,
                                isDefaultLocationOnline: meetingType.IsDefaultLocationOnline,
                                preBuffer: meetingType.PreBuffer,
                                postBuffer: meetingType.PostBuffer,
                                excludeFromSelfService,
                            };
                            //Create meeting extension
                            createMeetingTypeExtension(requestBody).then(() => {
                                setIsPerformingServiceUpdate(false);
                                return true;
                            });
                        }
                    });
                }
            }
        } catch (e) {
            trace.warn(e.message || 'updateMeetingTypePrivacyAction failed');
            updateMeetingTypePrivacyAction(
                meetingTypeId,
                !makePublic,
                makePublic ? !excludeFromSelfService : false
            );
            setIsPerformingServiceUpdate(false);
        }
    }
    return false;
}
